import {
  ActiveMediaFailedCode,
  AudioChangeAction,
  LeaveAudioSource,
  MutedSource
} from '@zoom/videosdk';
import LightCoolBg from 'assets/images/virtual-backgorunds/lifemd-bg-light-cool.webp';
import LightNeutralBg from 'assets/images/virtual-backgorunds/lifemd-bg-light-neutral.webp';
import LightWarmBg from 'assets/images/virtual-backgorunds/lifemd-bg-light-warm.webp';
import VirtualBg from 'assets/images/virtual-backgorunds/lifemd-bg.webp';
import { notifyError, notifySuccess } from 'components/common/Toast/Toast';
import { VirtualBackgroundsTypes } from 'pages/ZoomCall/Controls/SettingsPopup/VideoEffects/videEffects.types';

import {
  VideoCallTaskInfo,
  VirtualBackgroundConfig,
  ZoomCallContextProps
} from './zoomCallContext.types';

export const defaultVideoCallTaskInfo: VideoCallTaskInfo = {
  _id: '',
  advancedCallLogging: false,
  appointmentTime: '',
  category: '',
  isSukiEnabled: false,
  status: ''
};

/**
 * Default values for ZoomCallContext
 */
export const defaultValue: ZoomCallContextProps = {
  appointmentId: '',
  audioEnabled: true,
  backgroundSettings: VirtualBackgroundsTypes.None,
  callCRCDevice: () => Promise.resolve(),
  cancelCallCRCDevice: () => Promise.resolve(),
  handleEndVideoCall: () => {},
  interpreter: null,
  isConnectionError: false,
  isFullscreen: false,
  isLandscape: false,
  isLoadingBackground: false,
  isPiP: false, // Picture in Picture
  isVideoStarting: false,
  loggerClient: null,
  networkQualityLevel: { local: 3, remote: 3 },
  onChangeBackground: () => {},
  patientInfo: {
    _id: '',
    firstname: 'patient',
    fullname: 'patient',
    image: '',
    lastname: '',
    phone: '',
    profileImage: ''
  },
  remoteAudioEnabled: false,
  remoteParticipant: null,
  remoteParticipantDisconnected: false,
  remoteVideoState: 'unknown',
  room: null,
  setIsLandscape: () => {},
  setShowControls: () => {},
  showControls: true,
  showTimer: false,
  startVideo: () => Promise.resolve(),
  taskInfo: defaultVideoCallTaskInfo,
  toggleAudioEnabled: () => {},
  toggleFullscreen: () => {},
  toggleIsPiP: () => {},
  toggleVideoEnabled: () => {},
  videoEnabled: true
};

const IMAGE_MAPPING: Record<string, string> = {
  [VirtualBackgroundsTypes.Blue]: VirtualBg,
  [VirtualBackgroundsTypes.LightCool]: LightCoolBg,
  [VirtualBackgroundsTypes.LightNeutral]: LightNeutralBg,
  [VirtualBackgroundsTypes.LightWarm]: LightWarmBg
};

export const getVirtualBackgroundImageValue = (
  backgroundType: VirtualBackgroundsTypes
): string | 'blur' | undefined => {
  if (backgroundType === VirtualBackgroundsTypes.None) {
    return undefined;
  }

  if (backgroundType === VirtualBackgroundsTypes.Blur) {
    return 'blur';
  }

  return IMAGE_MAPPING[backgroundType];
};

export const setVirtualBackground = (
  isVirtualBGSupported: boolean,
  currentVB?: VirtualBackgroundsTypes,
  backgroundToApply?: VirtualBackgroundsTypes
): VirtualBackgroundConfig | undefined => {
  const background = backgroundToApply ?? currentVB ?? VirtualBackgroundsTypes.None;

  if (!isVirtualBGSupported || background === VirtualBackgroundsTypes.None) {
    return undefined;
  }

  // SDK 2.x startVideo: use 'blur' string for blur effect
  if (background === VirtualBackgroundsTypes.Blur) {
    return { virtualBackground: { imageUrl: 'blur' } };
  }

  const imageUrl = IMAGE_MAPPING[background];

  if (imageUrl) {
    return { virtualBackground: { imageUrl } };
  }

  return undefined;
};

/**
 * Handle audio state changes.
 */
export const handleCurrentAudioChange = (payload: {
  action: AudioChangeAction;
  source?: MutedSource | LeaveAudioSource;
}) => {
  const { action, source } = payload;

  if (action === AudioChangeAction.Leave) {
    if (source === LeaveAudioSource.EndedBySystem || source === LeaveAudioSource.MicrophoneError) {
      notifyError('Audio has ended due to a system error or microphone error');
      console.error('Audio ended by system or microphone error:', action, source);
    }
  } else if (action === AudioChangeAction.Muted) {
    if (source === MutedSource.PassiveByMuteOne || source === MutedSource.PassiveByMuteAll) {
      notifySuccess('Audio has been muted by the host');
      console.info('Audio muted by host:', action, source);
    }
  }
};

/**
 * Handle auto-play audio failure.
 * Occurs when browser blocks audio autoplay.
 */
export const handleAutoPlayAudioFailed = () => {
  notifyError(
    'Auto play audio failed. Please click anywhere on the page to resume audio playback.'
  );
  console.error('Auto play audio failed.');
};

/**
 * Handle active media failures.
 */
export const handleActiveMediaFailed = (payload: {
  code: ActiveMediaFailedCode;
  message: string;
}) => {
  switch (payload.code) {
    case ActiveMediaFailedCode.AudioConnectionFailed:
      notifyError('Refresh the browser and reconnect.');
      console.error('Audio connection failed:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.AudioStreamEnded:
      notifyError('Refresh the browser; the audio stream was interrupted.');
      console.error('Audio stream ended:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.MicrophonePermissionReset:
      notifyError('Grant the necessary microphone permissions.');
      console.error('Microphone permission reset:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.AudioStreamFailed:
      notifyError('Close all browsers and rejoin the meeting.');
      console.error('Audio stream failed:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.MicrophoneMuted:
      notifyError('Unmute the mic via system or browser settings.');
      console.error('Microphone muted:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.AudioStreamMuted:
      notifyError('The microphone was temporarily occupied. Rejoin the meeting.');
      console.error('Audio stream muted:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.AudioPlaybackInterrupted:
      notifyError('Audio playback was interrupted. Rejoin the meeting.');
      console.error('Audio playback interrupted:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.VideoConnectionFailed:
      notifyError('Refresh the browser.');
      console.error('Video connection failed:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.VideoStreamEnded:
      notifyError('Refresh the browser; the camera stream may have been blocked.');
      console.error('Video stream ended:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.CameraPermissionReset:
      notifyError('Grant camera permission.');
      console.error('Camera permission reset:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.WebGlContextInvalid:
      notifyError('Close all browsers and rejoin the meeting.');
      console.error('WebGL context invalid:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.WasmOutOfMemory:
      notifyError('Close all browsers and rejoin the meeting.');
      console.error('WASM out of memory:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.VideoStreamFailed:
      notifyError('Close all browsers and rejoin the meeting.');
      console.error('Video stream failed:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.VideoStreamMuted:
      notifyError('The camera was temporarily occupied. Rejoin the meeting.');
      console.error('Video stream muted:', payload.code, payload.message);
      break;
    case ActiveMediaFailedCode.SharingStreamFailed:
      notifyError('Close all browsers and rejoin the meeting.');
      console.error('Sharing stream failed:', payload.code, payload.message);
      break;
  }
};

/**
 * Handle device permission changes - notification only.
 * Notifies user when camera/microphone permissions are denied.
 *
 * Note: The actual device re-initialization when permissions are granted
 * is handled in zoomCallContext.tsx to have access to the room instance.
 */
export const notifyDevicePermissionDenied = (deviceName: 'microphone' | 'camera') => {
  notifyError(`Please allow ${deviceName} permission to continue.`);
};

/**
 * Polling interval in milliseconds for regular video statistics monitoring.
 * Used to continuously check remote participant video dimensions during a call.
 */
export const STATS_POLL_INTERVAL_MS = 500;

/**
 * Polling interval in milliseconds for burst video statistics monitoring.
 * Used during a 2-second window after orientation changes to quickly detect dimension updates.
 */
export const BURST_POLL_INTERVAL_MS = 200;

/**
 * Duration in milliseconds for burst mode polling window.
 * After an orientation change is detected, burst mode remains active for this duration
 * to quickly capture dimension updates from the video stream.
 */
export const BURST_DURATION_MS = 2000;

/**
 * Time window in milliseconds to consider events as "recent" when determining orientation.
 * When stats-based orientation differs from event-based orientation within this window,
 * the event-based orientation takes precedence to prevent flickering.
 */
export const EVENT_ORIENTATION_WINDOW_MS = 3000;
